import {
  Box,
  Chip,
  Divider,
  Link as MuiLink,
  ListItem,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { HighlightedSearchResultText } from '@backstage/plugin-search-react';
import { pageLabelFromLocation, ResultGroup } from './groupDocResults';

/** How many matching sections to list before summarising the rest. */
const MAX_SECTIONS_SHOWN = 5;

const useStyles = makeStyles(theme => ({
  sections: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  sectionLabel: {
    marginRight: theme.spacing(0.5),
  },
}));

/** Fields the TechDocs collator adds beyond the common document shape. */
type DocFields = { entityTitle?: string; name?: string; kind?: string };

/**
 * One documentation page in the result list, with the sections of that page
 * that also matched offered as jump links rather than as separate results.
 *
 * The heading is the page's title wherever it is known. A section's title is
 * its heading text, so a group whose best hit is a section would otherwise read
 * "Executive summary" or "Works cited" with nothing to say which document that
 * is. See groupDocResults.ts for why the grouping exists at all.
 */
export const GroupedDocSearchResultListItem = (props: {
  group: ResultGroup;
}) => {
  const classes = useStyles();
  const { primary, sections, pageTitle } = props.group;
  const document = primary.document as typeof primary.document & DocFields;
  const highlight = primary.highlight;

  const entity = document.entityTitle || document.name;
  // With no page-level hit in this window there is no page title, so the file
  // name identifies the document instead of leaving a bare heading like
  // "Works cited" to stand on its own.
  const pageLabel = pageTitle
    ? undefined
    : pageLabelFromLocation(primary.document.location);
  const heading = pageTitle || document.title;
  const shown = sections.slice(0, MAX_SECTIONS_SHOWN);
  const hidden = sections.length - shown.length;

  return (
    <>
      <ListItem alignItems="flex-start">
        <ListItemText
          primaryTypographyProps={{ variant: 'h6' }}
          primary={
            <Link to={primary.document.location}>
              {pageLabel && (
                <Typography component="span" variant="h6" color="textSecondary">
                  {`${pageLabel} › `}
                </Typography>
              )}
              {heading}
              {entity && (
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                >
                  {` | ${entity} docs`}
                </Typography>
              )}
            </Link>
          }
          secondary={
            <Typography
              component="span"
              variant="body2"
              color="textSecondary"
              noWrap
              display="block"
            >
              {highlight?.fields.text ? (
                <HighlightedSearchResultText
                  text={highlight.fields.text}
                  preTag={highlight.preTag}
                  postTag={highlight.postTag}
                />
              ) : (
                primary.document.text
              )}
            </Typography>
          }
        />
        {sections.length > 0 && (
          <Box display="flex" alignItems="center" flexShrink={0} ml={2}>
            <Chip
              label={`${sections.length + 1} matches`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </ListItem>
      {shown.length > 0 && (
        <Box className={classes.sections}>
          <Typography
            variant="caption"
            color="textSecondary"
            className={classes.sectionLabel}
          >
            also in:
          </Typography>
          {shown.map(section => (
            <Typography
              key={section.document.location}
              variant="caption"
              component="span"
            >
              <MuiLink href={section.document.location}>
                {section.document.title}
              </MuiLink>
            </Typography>
          ))}
          {hidden > 0 && (
            <Typography variant="caption" color="textSecondary">
              {`and ${hidden} more`}
            </Typography>
          )}
        </Box>
      )}
      <Divider component="li" />
    </>
  );
};
